import TextField from '@decisiv/ui-components/lib/components/TextField';
import { t } from '@lingui/macro';
import { Button, Grid } from '@decisiv/ui-components';
import iconix from '@decisiv/iconix';
import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
import { FormGroup } from '../../pages/IntegrationDetailPage/StyledComponents';
import StepFunctionExecutionsApi from '../../api/step_function_executions';
import InvocationPayload from './InvocationPayload';

export default function TraceIdTab({
  submitFunction,
  buttonLoading,
  integration,
}) {
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
  const [traceId, setTraceId] = useState('');
  const [dealerId, setDealerId] = useState('');
  const [locationName, setLocationName] = useState('');
  const [srmAccountId, setSrmAccountId] = useState('');
  const [traceIdWarning, setTraceIdWarning] = useState('');
  const [disableInput, setDisableInput] = useState(false);
  const [invocationInput, setInvocationInput] = useState('');
  const [originalSampleInput, setOriginalSampleInput] = useState('');
  const [configuredIntegrationId, setConfiguredIntegrationId] = useState('');
  const [selectedTriggerType, setSelectedTriggerType] = useState('');
  const [loading, setLoading] = useState(false);

  const resetPayload = () => {
    setInvocationInput(originalSampleInput);
  };

  const resetForm = () => {
    setLocationName('');
    setDealerId('');
    setSrmAccountId('');
    setDisableInput(false);
    setConfiguredIntegrationId('');
    setSelectedTriggerType('');
    setSubmitButtonDisabled(true);
    setLoading(false);
  };

  const handleTraceIdChange = async (e) => {
    const formTraceId = e.target.value;
    setTraceId(formTraceId);
    setTraceIdWarning('');

    if (formTraceId.length === 36) {
      setLoading(true);
      setDisableInput(true);
      const responseStepFunctionExecution =
        await StepFunctionExecutionsApi.fetchStepFunctionExecutionsByTraceId(
          formTraceId,
          integration.id,
        );

      if (
        responseStepFunctionExecution.data &&
        responseStepFunctionExecution.data.length > 0
      ) {
        const stepFunctionExecution =
          responseStepFunctionExecution.data[0].attributes;

        // Slim list row omits invocation_payload; fetch the full record.
        const detailResponse =
          await StepFunctionExecutionsApi.fetchStepFunctionExecutionById(
            responseStepFunctionExecution.data[0].id,
          );
        const rawInvocationPayload =
          detailResponse.data?.attributes?.invocation_payload;

        if (!rawInvocationPayload) {
          setTraceIdWarning('TraceId Not found');
          resetForm();
          return;
        }

        let invocationPayload;
        try {
          invocationPayload = JSON.parse(rawInvocationPayload);
        } catch {
          setTraceIdWarning('TraceId Not found');
          resetForm();
          return;
        }

        setDisableInput(false);

        setDealerId(stepFunctionExecution.dealer_id);
        setLocationName(stepFunctionExecution.location_name);
        setSrmAccountId(stepFunctionExecution.srm_account_id);

        if (stepFunctionExecution.trigger_type === 'application_event') {
          delete invocationPayload.metadata;
          setInvocationInput(JSON.stringify(invocationPayload));
          setOriginalSampleInput(JSON.stringify(invocationPayload));
        } else {
          setInvocationInput(JSON.stringify(invocationPayload.data.attributes));
          setOriginalSampleInput(
            JSON.stringify(invocationPayload.data.attributes),
          );
        }

        setConfiguredIntegrationId(
          stepFunctionExecution.configured_integration_id,
        );
        if (stepFunctionExecution.trigger_type === 'application_event') {
          setSelectedTriggerType(
            stepFunctionExecution.action_or_application_event,
          );
        } else {
          setSelectedTriggerType('http');
        }
        setSubmitButtonDisabled(false);
        setLoading(false);
      } else {
        setTraceIdWarning('TraceId Not found');
        resetForm();
      }
    } else {
      resetForm();
    }
  };

  const onSelectTriggerType = (e) => {
    setSelectedTriggerType(e.text);
  };

  return (
    <Grid.Container>
      <Grid.Row>
        <Grid.Column span="4" style={{ minHeight: '800px' }}>
          <br />
          <FormGroup key="trace-id-form">
            <TextField
              label={t`Trace-ID`}
              name="trace-id"
              value={traceId}
              onChange={handleTraceIdChange}
              warningMessage={traceIdWarning}
              disabled={disableInput}
              maxLength={36}
            />
          </FormGroup>
          <FormGroup key="trace-id-location-name-form">
            <TextField
              label={t`Location Name`}
              name="trace-location-name"
              value={locationName}
              disabled
            />
          </FormGroup>
          <FormGroup key="trace-id-srm-account-form">
            <TextField
              label={t`SRM Account Id`}
              name="trace-srm-account-id"
              value={srmAccountId}
              disabled
            />
          </FormGroup>
          <FormGroup key="trace-id-dealer-id-form">
            <TextField
              label={t`Dealer ID`}
              name="trace-dealer-id"
              value={dealerId}
              disabled
            />
          </FormGroup>
          <FormGroup key="trace-id-submit">
            <Button
              aria-label={t`Run Manual Execution`}
              text={t`Run Manual Execution`}
              icon={iconix.ArrowRight}
              iconPosition="right"
              disabled={submitButtonDisabled}
              loading={buttonLoading}
              loadingText={t`Running`}
              onClick={() =>
                submitFunction(
                  invocationInput,
                  configuredIntegrationId,
                  selectedTriggerType,
                  srmAccountId,
                  dealerId,
                )
              }
            />
          </FormGroup>
        </Grid.Column>
        <InvocationPayload
          invocationInput={invocationInput}
          disableTriggerType
          selectedTriggerType={selectedTriggerType}
          onSelectTriggerType={onSelectTriggerType}
          setInvocationInput={setInvocationInput}
          loading={loading}
          resetPayload={resetPayload}
          integration={integration}
        />
      </Grid.Row>
    </Grid.Container>
  );
}

TraceIdTab.propTypes = {
  integration: PropTypes.oneOfType([PropTypes.object]).isRequired,
  buttonLoading: PropTypes.bool.isRequired,
  submitFunction: PropTypes.func.isRequired,
};
